// Unpublished Work © 2021-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import EditLink from 'Ui/components/common/data-table/edit-link';
import EquipmentName from 'OnEquip/equipment/common/data-table/equipment-name';
import OnLinkProgressBar from 'Ui/components/common/onlink-progress-bar';
import {deleteService, skipService} from 'Services/maintenance-service';
import {getMaintenanceDue} from 'Services/membership-service';
import {fetchEffectData} from 'Utils/react-utils';
import {
    alphaNumericCompare,
    dateCompare,
    dueColumnCompare,
    formatDateSelect,
    formatEquipmentName,
    formatModelAndManufacturerName,
    genericServiceCompare,
    getInterval,
    getPercentClassName,
    getPercentDue,
    numericCompare
} from 'Ui/models/maintenance';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import accounting from 'accounting';
import dialogTypes from 'Ui/components/common/dialog-types';
import {useEquipmentContext} from 'OnEquip/equipment/context/equipment-context';
import {MAINTENANCE} from 'OnEquip/equipment/constants/equipment-details-tabs';
import permissionsConstants from 'Common/constants/permissions';
import {RECOMMENDED} from 'Common/constants/service-status';
import {hasPermissions} from 'Common/utils/permission-handler';
import {SUGGESTED_MAINTENANCE} from 'Common/constants/routes';
import {
    createOnContinueCallback, displayErrorMessage,
    getDeleteSelectedServiceConfirmationMessage,
    modifyServices
} from 'Utils/maintenance-utils';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {PARTS_CATALOG_URL, SHOP_URL} from 'Ui/components/secondary-navbar/utils/dropdown-navbar-helper';
import {redirect} from 'Utils/redirect-utils';
import {
    EDIT_SERVICE,
    MANAGE_EQUIPMENT_SETTINGS,
    MANAGE_SERVICE,
    VIEW_SERVICE
} from 'Common/constants/business-activities';
import {isShopDeereUrlEnabled} from 'Src/ui-core/pages/onequip/parts/inventory';
import {IconAdd, IconDelete, IconGear, IconNewWindow} from '@deere/icons';
import {MAINTENANCE_MANAGER_PROGOLF} from 'Common/constants/isg-toggles';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';
import {Box} from '@deere/isg.component-library';

const DECIMAL_TO_PERCENT = 100;

function getDataGridColumns(translations, membershipId, history) {
    const onequipUrl = `${window.props.onLinkLegacyUri}/onequip/#m/${membershipId}/fleet/{0}/suggestedMaintenance`;

    const equipmentUrl = `${onequipUrl}/equipment/{1}`;
    const serviceUrl = `${SUGGESTED_MAINTENANCE}/serviceForm?serviceId={0}`;

    return [
        {
            headerName: translations.EQUIPMENT_LIST,
            field: 'formattedName',
            minWidth: 150,
            flex: 1,
            valueGetter: (_value, row) => row,
            sortComparator: (a, b) => genericServiceCompare(a, b, alphaNumericCompare, 'formattedName'),
            renderCell: (params) => {
                return (
                    <EquipmentName
                        equipment={params.row}
                        tab={MAINTENANCE}
                        type={SUGGESTED_MAINTENANCE}
                        url={equipmentUrl}
                    />
                );
            }
        },
        {
            headerName: translations.MODEL,
            field: 'modelAndManufacturerName',
            minWidth: 150,
            flex: 1,
            valueGetter: (_value, row) => row,
            sortComparator: (a, b) => genericServiceCompare(a, b, alphaNumericCompare, 'modelAndManufacturerName'),
            renderCell: (params) => params.row.modelAndManufacturerName
        },
        {
            headerName: translations.INTERVAL,
            field: 'interval',
            minWidth: 150,
            flex: 1,
            valueGetter: (_value, row) => row,
            sortComparator: (a, b) => genericServiceCompare(a, b, alphaNumericCompare, 'interval'),
            renderCell: (params) => params.row.interval
        },
        {
            headerName: translations.ONLINK_SERVICE,
            field: 'serviceTypeName',
            minWidth: 150,
            flex: 1,
            valueGetter: (_value, row) => row,
            sortComparator: (a, b) => genericServiceCompare(a, b, alphaNumericCompare, 'serviceTypeName'),
            renderCell: (params) => {
                const service = params.row;
                const filledInServiceUrl = serviceUrl.replace('{0}', service.serviceId);

                return (
                    <EditLink
                        onClick={() => history.push(filledInServiceUrl)}
                        requiredMyJdPermissions={[VIEW_SERVICE]}
                    >
                        {service.serviceTypeName}
                    </EditLink>
                );
            }
        },
        {
            headerName: translations.ONLINK_DUE_DATE,
            field: 'formattedAtDateSelect',
            width: 150,
            valueGetter: (_value, row) => row,
            sortComparator: (a, b) => genericServiceCompare(a, b, dateCompare, 'atDateSelect'),
            renderCell: (params) => {
                return params.row.formattedAtDateSelect ?? '';
            }
        },
        {
            headerName: translations.ONLINK_DUE_HOURS,
            field: 'atHours',
            type: 'number',
            width: 150,
            valueGetter: (_value, row) => row,
            sortComparator: (a, b) => genericServiceCompare(a, b, numericCompare, 'atHours'),
            renderCell: (params) => {
                return params.row.atHours ?? '';
            }
        },
        {
            headerName: translations.ONLINK_CURRENT_HOURS,
            field: 'totalHours',
            type: 'number',
            width: 150,
            valueGetter: (_value, row) => row,
            sortComparator: (a, b) => genericServiceCompare(a, b, numericCompare, 'totalHours'),
            renderCell: (params) => {
                return accounting.formatNumber(params.row.totalHours, 1);
            }
        },
        {
            headerName: translations.DUE,
            field: 'dueProgressBar',
            width: 100,
            align: 'center',
            valueGetter: (_value, row) => row,
            sortComparator: dueColumnCompare,
            renderCell: (params) => {
                const progress = params.row.percentDue;
                const progressClassName = getPercentClassName(params.row);

                return (
                    <Box
                        alignItems='center'
                        display='flex'
                        height='100%'
                    >
                        <OnLinkProgressBar
                            barClassName='maintenance-progress-bar'
                            fillClassName={progressClassName}
                            percentFilled={Math.min(1, progress) * DECIMAL_TO_PERCENT}
                        />
                    </Box>
                );
            }
        }
    ];
}

function initializeState() {
    const [services, setServices] = React.useState(() => []);
    const [loading, setLoading] = React.useState(() => false);
    const [selectedServices, setSelectedServices] = React.useState(() => new Set());

    return {
        services,
        setServices,
        loading,
        setLoading,
        selectedServices,
        setSelectedServices
    };
}

function getNavBarActions(openServiceDialog, translations, loading, selectedServices,
    onSkipServicesClick, onDeleteServicesClick, history, isMigrated, country) {
    return [
        {
            onClick: () => openServiceDialog({
                translations
            }),
            disabled: loading,
            Icon: <IconAdd/>,
            title: 'ONLINK_SERVICE',
            variant: 'primary',
            requiredMyJdPermissions: EDIT_SERVICE
        },
        {
            disabled: selectedServices.size === 0,
            onClick: onSkipServicesClick,
            title: 'ONLINK_SKIP_SELECTED',
            requiredMyJdPermissions: MANAGE_SERVICE
        },
        {
            disabled: selectedServices.size === 0,
            onClick: onDeleteServicesClick,
            Icon: <IconDelete/>,
            title: 'ONLINK_DELETE_SELECTED',
            requiredMyJdPermissions: MANAGE_SERVICE
        },
        {
            onClick: () => window.open(isShopDeereUrlEnabled({
                country,
                isMigrated
            }) ? SHOP_URL : PARTS_CATALOG_URL, '_blank'),
            title: 'SHOP_FOR_PARTS',
            Icon: <IconNewWindow/>
        },
        {
            onClick: () => redirect(`${SUGGESTED_MAINTENANCE}/settings/fleet`, history),
            title: 'SETTINGS',
            Icon: <IconGear/>,
            requiredMyJdPermissions: MANAGE_EQUIPMENT_SETTINGS
        }
    ];
}

function getFilterComponent(
    translations,
    openConfirmation,
    closeConfirmation,
    onDelete,
    onSkip,
    openServiceDialog,
    selectedServices,
    loading,
    history,
    isMigrated,
    country,
    canEditServices
) {
    const sharedConfirmationProps = {
        translations,
        onCancel: closeConfirmation,
        showLoadingIcon: true
    };

    const onDeleteServicesClick = () => {
        openConfirmation({
            ...sharedConfirmationProps,
            message: getDeleteSelectedServiceConfirmationMessage(translations),
            onContinue: createOnContinueCallback(closeConfirmation, onDelete),
            title: translations.DELETE_MANTENANCE
        });
    };

    const onSkipServicesClick = () => {
        openConfirmation({
            ...sharedConfirmationProps,
            message: translations.ONLINK_MAINTENANCE_SKIP_CONFIRMATION_MESSAGE,
            onContinue: createOnContinueCallback(closeConfirmation, onSkip),
            title: translations.ONLINK_SKIP_MAINTENANCE
        });
    };

    useNavBarActions(
        canEditServices ?
            getNavBarActions(
                openServiceDialog,
                translations,
                loading,
                selectedServices,
                onSkipServicesClick,
                onDeleteServicesClick,
                history,
                isMigrated,
                country
            ) :
            []
    );
}

function Maintenance(props) {
    const {
        closeConfirmation,
        history,
        isgToggles,
        isMigrated,
        membershipId,
        openConfirmation,
        permissions,
        superUser,
        translations,
        openServiceDialog,
        country
    } = props;

    const isFleetToMaintenanceEnabled = isgToggles?.[MAINTENANCE_MANAGER_PROGOLF];

    const {
        services,
        setServices,
        loading,
        setLoading,
        selectedServices,
        setSelectedServices
    } = initializeState(props);

    const {addToast} = useToast();
    const equipmentContext = useEquipmentContext();

    const canDeleteServices = isMigrated || hasPermissions(
        {
            permissionMap: permissions,
            superUser
        },
        permissionsConstants.DELETE_SERVICE_TICKETS
    );

    equipmentContext.current = async (isMounted = () => true) => {
        setSelectedServices(new Set());
        setLoading(true);

        const services = await getMaintenanceDue(RECOMMENDED);

        if (isMounted()) {
            const formattedServices = services.map((service) => ({
                ...service,
                percentDue: getPercentDue(service),
                interval: getInterval(service, translations),
                modelAndManufacturerName: formatModelAndManufacturerName(service),
                formattedName: formatEquipmentName(service),
                formattedAtDateSelect: formatDateSelect(service)
            }));

            setServices(formattedServices);
            setLoading(false);
        }
    };

    async function onDelete() {
        const rejectedResults = await modifyServices(services, selectedServices, deleteService);

        if (rejectedResults.length !== 0) {
            const rejectedNames = rejectedResults.join(', ');

            displayErrorMessage(addToast, translations.ONLINK_MAINTENANCE_DELETE_ERROR, rejectedNames);
        }

        await equipmentContext.current();
    }

    async function onSkip() {
        const rejectedResults = await modifyServices(
            services,
            selectedServices,
            (serviceId) => skipService(serviceId)
        );

        if (rejectedResults.length !== 0) {
            const rejectedNames = rejectedResults.join(', ');

            displayErrorMessage(addToast, translations.ONLINK_MAINTENANCE_SKIP_ERROR, rejectedNames);
        }

        await equipmentContext.current();
    }

    React.useEffect(() => fetchEffectData(equipmentContext.current), [membershipId]);

    const canEditServices = canDeleteServices && !isFleetToMaintenanceEnabled;

    getFilterComponent(
        translations,
        openConfirmation,
        closeConfirmation,
        onDelete,
        onSkip,
        openServiceDialog,
        selectedServices,
        loading,
        history,
        isMigrated,
        country,
        canEditServices
    );

    return (
        <DataGridWrapper
            columns={getDataGridColumns(translations, membershipId, history)}
            disableRowSelectionOnClick
            getRowId={(row) => row.serviceId}
            initialState={{
                sorting: {
                    sortModel: [{
                        field: 'dueProgressBar',
                        sort: 'asc'
                    }]
                }
            }}
            loading={loading}
            onRowSelectionModelChange={(model) => setSelectedServices(model.ids)}
            rowSelectionMode={canDeleteServices && !isFleetToMaintenanceEnabled ? 'multiple' : ''}
            rowSelectionModel={{
                type: 'include',
                ids: selectedServices
            }}
            rows={services}
            searchFields={['formattedName', 'modelAndManufacturerName', 'interval', 'serviceTypeName', 'formattedAtDateSelect', 'atHours', 'totalHours']}
            searchable={true}
            slotProps={{
                root: {
                    disableColumnMenu: true
                }
            }}
            sx={{
                '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
                    display: 'none'
                }
            }}
            translations={translations}
        />
    );
}

Maintenance.propTypes = {
    closeConfirmation: PropTypes.func,
    closeDialog: PropTypes.func,
    country: PropTypes.string,
    history: PropTypes.history,
    isgToggles: PropTypes.object,
    isMigrated: PropTypes.bool,
    membershipId: PropTypes.string,
    openConfirmation: PropTypes.func,
    openDialog: PropTypes.func,
    openServiceDialog: PropTypes.func,
    permissions: PropTypes.legacyPermissions,
    superUser: PropTypes.bool,
    translations: PropTypes.translations,
    updateOnDeleteClick: PropTypes.func,
    updateOnSkipClick: PropTypes.func
};

export function mapStateToProps(state) {
    return {
        isgToggles: state.account.isgToggles,
        isMigrated: state.membership.isMigrated,
        membershipId: state.membership.membershipId,
        permissions: state.account.permissions,
        superUser: state.account.superUser,
        country: state.membership.country
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        openServiceDialog(props) {
            dispatch(openDialog(dialogTypes.SERVICE_FORM_DIALOG, props));
        },
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Maintenance));
